import React, { useState, useEffect } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import { useAlert } from "../../contexts/AlertContext";
import { adminAPI } from "../../services/api";
import { User } from "../../types";
import {
  MdPeople,
  MdSearch,
  MdDelete,
  MdAdminPanelSettings,
  MdPerson,
  MdRefresh,
} from "react-icons/md";

const UserManagement = () => {
  const { addAlert } = useAlert();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const PeopleIcon = MdPeople as React.FC<React.SVGProps<SVGSVGElement>>;
  const SearchIcon = MdSearch as React.FC<React.SVGProps<SVGSVGElement>>;
  const DeleteIcon = MdDelete as React.FC<React.SVGProps<SVGSVGElement>>;
  const AdminIcon = MdAdminPanelSettings as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const PersonIcon = MdPerson as React.FC<React.SVGProps<SVGSVGElement>>;
  const RefreshIcon = MdRefresh as React.FC<React.SVGProps<SVGSVGElement>>;

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getAllUsers();
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error: any) {
      addAlert({ type: "error", message: "Failed to fetch users" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const filtered = users.filter(
      (user) =>
        user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleMakeAdmin = async (userId: number) => {
    try {
      await adminAPI.makeAdmin({ user_id: userId });
      addAlert({
        type: "success",
        message: "User promoted to admin successfully",
      });
      fetchUsers();
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to make user admin",
      });
    }
  };

  const handleRemoveAdmin = async (userId: number) => {
    try {
      await adminAPI.removeAdmin({ user_id: userId });
      addAlert({
        type: "success",
        message: "Admin privileges removed successfully",
      });
      fetchUsers();
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to remove admin",
      });
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this user? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      await adminAPI.deleteUser({ user_id: userId });
      addAlert({ type: "success", message: "User deleted successfully" });
      fetchUsers();
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to delete user",
      });
    }
  };

  const formatBalance = (balance: string | number): string => {
    const num = typeof balance === "string" ? parseFloat(balance) : balance;
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  const getStatusBadge = (user: User) => {
    if (!user.is_active) {
      return (
        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full dark:bg-red-900 dark:text-red-200">
          Suspended
        </span>
      );
    }
    if (user.kyc_status === "verified") {
      return (
        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full dark:bg-green-900 dark:text-green-200">
          KYC Verified
        </span>
      );
    }
    if (user.kyc_status === "pending") {
      return (
        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full dark:bg-yellow-900 dark:text-yellow-200">
          KYC Pending
        </span>
      );
    }
    return (
      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full dark:bg-gray-900 dark:text-gray-200">
        Unverified
      </span>
    );
  };

  const getRoleBadge = (user: User) => {
    return user.role === "admin" ? (
      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full dark:bg-purple-900 dark:text-purple-200">
        Admin
      </span>
    ) : (
      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full dark:bg-blue-900 dark:text-blue-200">
        User
      </span>
    );
  };

  return (
    <div className={GlobalStyles.container}>
      {/* Header */}
      <div
        className={`${CommonClasses.flexBetween} max-sm:flex-col max-sm:items-start max-sm:gap-4`}
      >
        <div>
          <h1
            className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-2`}
          >
            User Management
          </h1>
          <p className={`${CommonClasses.textGray}`}>
            Manage platform users, permissions, and access levels.
          </p>
        </div>
        <button
          onClick={fetchUsers}
          disabled={isLoading}
          className={`${GlobalStyles.buttonIconOutline} ${
            isLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <RefreshIcon
            className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
          />
        </button>
      </div>

      {/* Search and Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <div className={GlobalStyles.inputWrapper}>
            <SearchIcon className={GlobalStyles.inputIcon} />
            <input
              type="text"
              placeholder="Search users by name, email, or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={GlobalStyles.input}
            />
          </div>
        </div>
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className={`${CommonClasses.textXl} font-bold text-primary`}>
            {users.length}
          </div>
          <div className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
            Total Users
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className={GlobalStyles.tableContainer}>
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className={`${CommonClasses.textGray} mt-4`}>Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-8">
            <PeopleIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className={`${CommonClasses.textGray} mb-2`}>No users found</p>
            <p className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
              {searchTerm
                ? "Try adjusting your search terms"
                : "No users registered yet"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className={GlobalStyles.table}>
              <thead className={GlobalStyles.tableHeader}>
                <tr>
                  <th className={GlobalStyles.tableHeaderCell}>User</th>
                  <th className={GlobalStyles.tableHeaderCell}>Status</th>
                  <th className={GlobalStyles.tableHeaderCell}>Balances</th>
                  <th className={GlobalStyles.tableHeaderCell}>Role</th>
                  <th className={GlobalStyles.tableHeaderCell}>Joined</th>
                  <th className={GlobalStyles.tableHeaderCell}>Actions</th>
                </tr>
              </thead>
              <tbody className={GlobalStyles.tableBody}>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className={GlobalStyles.tableRow}>
                    <td className={GlobalStyles.tableCell}>
                      <div className={`${CommonClasses.flexStart} gap-3`}>
                        <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">
                            {user.first_name[0]}
                            {user.last_name[0]}
                          </span>
                        </div>
                        <div>
                          <div
                            className={`font-medium ${CommonClasses.textGrayStrong}`}
                          >
                            {user.first_name} {user.last_name}
                          </div>
                          <div
                            className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                          >
                            @{user.username}
                          </div>
                          <div
                            className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                          >
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div className="space-y-1">
                        {getStatusBadge(user)}
                        <div
                          className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                        >
                          Level {user.account_level}
                        </div>
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div className="space-y-1">
                        <div
                          className={`${CommonClasses.textSm} ${CommonClasses.textGrayStrong}`}
                        >
                          Ledger:{" "}
                          <span className="font-medium">
                            ${formatBalance(user.ledger_balance)}
                          </span>
                        </div>
                        <div
                          className={`${CommonClasses.textSm} ${CommonClasses.textGrayStrong}`}
                        >
                          Stake:{" "}
                          <span className="font-medium">
                            ${formatBalance(user.stake_balance)}
                          </span>
                        </div>
                        <div
                          className={`${CommonClasses.textSm} ${CommonClasses.textGrayStrong}`}
                        >
                          Bonus:{" "}
                          <span className="font-medium">
                            ${formatBalance(user.bonus_balance)}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      {getRoleBadge(user)}
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div
                        className={`${CommonClasses.textSm} ${CommonClasses.textGrayStrong}`}
                      >
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                      <div
                        className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                      >
                        {new Date(user.created_at).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div className={`${CommonClasses.flexStart} gap-2`}>
                        {user.role === "user" ? (
                          <button
                            onClick={() => handleMakeAdmin(user.id)}
                            className={`${GlobalStyles.buttonIconPrimary}`}
                            title="Make Admin"
                          >
                            <AdminIcon className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleRemoveAdmin(user.id)}
                            className={`${GlobalStyles.buttonIconOutline}`}
                            title="Remove Admin"
                          >
                            <PersonIcon className="w-4 h-4" />
                          </button>
                        )}

                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className={`${GlobalStyles.buttonIconDanger}`}
                          title="Delete User"
                        >
                          <DeleteIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
